import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import {
  IonRouterOutlet,
  IonApp,
  Platform,
} from '@ionic/angular/standalone';
import { Keyboard } from '@capacitor/keyboard';
import { register } from 'swiper/element/bundle';
import { CartService } from '@infra-adapters/services/cart.service';
import { AnalyticsService } from '@infra-adapters/services/analytics.service';
import { NavigationEnd, Router } from '@angular/router';
import { AppEvents } from '@core-interfaces/global/events';
import { filter, Subject, takeUntil } from 'rxjs';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { PaymentBrowserService } from '@infra-adapters/services/payment-browser.service';
import { ClarityService } from '@infra-adapters/services/clarity.service';
import { DebugOverlayComponent } from '@infra-ui/components/debug-overlay/debug-overlay.component';
import { AppVersionFacade } from '@infra-ui/facades/app-version.facade';
import { AuthService } from '@infra-adapters/services/auth.service';


register();
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonApp, IonRouterOutlet, DebugOverlayComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private currentScreen: string | null = null;
  private screenEnterTime: number = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private platform: Platform,
    private cartService: CartService,
    private analyticsService: AnalyticsService,
    private router: Router,
    private zone: NgZone,
    private paymentBrowserService: PaymentBrowserService,
    private clarityService: ClarityService,
    private appVersionFacade: AppVersionFacade,
    private authService: AuthService,
  ) {
    this.platform.ready().then(() => {
      if (this.platform.is('ios') || this.platform.is('android') || this.platform.is('mobile')) {
        document.documentElement.classList.add('mobile');
      }
      this.analyticsService.initializeFirebase();
    });
  }

  async ngOnInit() {
    await this.platform.ready();
    void this.appVersionFacade.start();
    await this.cartService.loadCartItems();

    // Inicializar Clarity lo más temprano posible
    this.clarityService.init();

    if (this.platform.is('capacitor')) {
      Keyboard.addListener('keyboardWillShow', () => {
        document.body.classList.add('keyboard-is-open');
      });

      Keyboard.addListener('keyboardWillHide', () => {
        document.body.classList.remove('keyboard-is-open');
      });

      this.initializeDeepLinking();
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          this.zone.run(() => {
            void this.authService.renewSessionIfPossible();
          });
        }
      });
    }

    void this.authService.renewSessionIfPossible();
    this.initScreenTracking();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void this.authService.renewSessionIfPossible();
    }
  };

  private initScreenTracking() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const screenName = event.urlAfterRedirects;
        if (this.currentScreen) {
          const duration = Date.now() - this.screenEnterTime;
          this.analyticsService.logEvent(AppEvents.screen_view, {
            screen_name: this.currentScreen,
            screen_duration_ms: duration
          });
        }
        this.currentScreen = screenName;
        this.screenEnterTime = Date.now();
      });
  }

  private initializeDeepLinking() {
    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {

        try {
          const urlObj = new URL(event.url);
          const path = urlObj.hostname + urlObj.pathname;

          const queryParams: any = {};
          urlObj.searchParams.forEach((value, key) => {
            queryParams[key] = value;
          });

          console.log('Navegando a:', path, 'con params:', queryParams);

          if (path === 'payment-result' || path.startsWith('payment-result')) {
            console.log('Payment result deep link received, closing browser...');
            await this.paymentBrowserService.handleDeepLinkReturn();

            const collectionStatus = queryParams['collection_status'];
            const orderId = queryParams['order_id'] || queryParams['external_reference'];
            const hasOrderId = Boolean(
              orderId &&
              orderId !== 'null' &&
              orderId !== 'undefined'
            );
            const isCancelledOrRejected = (!collectionStatus || collectionStatus === 'null') && !hasOrderId;

            if (isCancelledOrRejected) {
              console.log('Payment cancelled or rejected, redirecting to cart');
              this.router.navigate(['/carrito'], { replaceUrl: true });
              return;
            }
          }

          this.router.navigate([path], { queryParams, replaceUrl: true });
        } catch (error) {
          console.error('Error procesando deep link:', error);
        }
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    App.removeAllListeners();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    // Cleanup de listeners del Keyboard registrados en ngOnInit.
    // Si la app se rebootea (hot reload en dev, o Capacitor reusa el WebView),
    // los handlers stale dejaban la clase `keyboard-is-open` pegada al body
    // y rompían el cálculo de altura del ion-content.
    if (this.platform.is('capacitor')) {
      Keyboard.removeAllListeners();
    }
    // Defensa adicional: garantizar que la clase no quede pegada.
    document.body.classList.remove('keyboard-is-open');
  }
}
